import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown, ShieldCheck, Globe, TrendingUp } from "lucide-react";
import { Particles } from "./Particles";
import { FlowLines } from "./FlowLines";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const fade = useTransform(scrollYProgress, [0.6, 0.98], [1, 0.25]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const glowX = useSpring(mx, { stiffness: 38, damping: 18 });
  const glowY = useSpring(my, { stiffness: 38, damping: 18 });

  const onMouseMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 30);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 30);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="noise-overlay relative flex min-h-screen items-center overflow-hidden bg-navy-950 pt-28 pb-20"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.09),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(22,35,61,0.9),transparent_60%)]" />
      <FlowLines />
      <Particles />
      <motion.div
        aria-hidden="true"
        style={{ x: glowX, y: glowY }}
        className="absolute left-1/2 top-1/3 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gold/10 blur-[140px]"
      />

      <motion.div
        style={reduced ? undefined : { y: yContent, opacity: fade }}
        className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16"
      >
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="mb-7 inline-flex items-center gap-3 rounded-full border border-gold/25 bg-navy-900/60 px-5 py-2 backdrop-blur-md"
          >
            <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
            <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.28em] text-gold-soft">
              UAE Business Consultancy &amp; Digital Solutions
            </span>
          </motion.div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.06] tracking-tight text-slate-50">
            <MaskedLine delay={0.5}>From Compliance</MaskedLine>
            <MaskedLine delay={0.66}>
              to <span className="text-gold-metallic italic">Growth.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="mt-6 font-display text-lg sm:text-xl italic text-gold-soft/95"
          >
            Your Business. Our Expertise. Your Growth.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.12, ease: EASE }}
            className="mt-4 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400"
          >
            Faithh Business Consultancy is your UAE partner for VAT &amp; corporate tax compliance,
            websites and mobile apps, digital marketing, advertising and branding — everything your
            business needs to stay compliant, look world-class and grow.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.26, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-consultation"
              className="btn-gold inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm sm:text-base font-semibold text-navy-950 transition-[box-shadow,transform] duration-400 hover:-translate-y-1 hover:shadow-[0_18px_50px_-10px_rgba(212,175,55,0.55)]"
            >
              Get a Free Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2.5 rounded-full border border-gold/35 px-8 py-4 text-sm sm:text-base font-semibold text-gold-soft transition-[background-color,border-color,color] duration-400 hover:border-gold hover:bg-gold/10 hover:text-gold-bright"
            >
              Explore Our Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.6 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500"
          >
            <span>VAT &amp; Corporate Tax</span>
            <span className="h-1 w-1 rotate-45 bg-gold/50" />
            <span>Web &amp; Apps</span>
            <span className="h-1 w-1 rotate-45 bg-gold/50" />
            <span>Marketing &amp; Branding</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
          className="relative mt-4 lg:col-span-5 lg:mt-0"
        >
          <div aria-hidden="true" className="animate-spin-slow absolute -inset-6 rounded-full border border-dashed border-gold/20 sm:-inset-10" />
          <div aria-hidden="true" className="absolute -inset-3 rounded-[2rem] bg-gold/10 blur-2xl" />
          <div className="sheen-border relative overflow-hidden rounded-[1.75rem] border border-gold/20">
            <img
              src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1200&q=80"
              alt="Dubai business skyline at dusk"
              className="h-[300px] w-full object-cover sm:h-[380px] lg:h-[460px]"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-navy-950/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 rounded-2xl glass-panel px-5 py-4">
              <img src="/assets/faithh-logo.png" alt="" className="h-9 w-9 shrink-0 rounded-full bg-white/95 object-contain p-0.5 ring-1 ring-gold/40" />
              <div className="min-w-0">
                <p className="truncate font-display text-sm font-semibold text-slate-50">Faithh Business Consultancy</p>
                <p className="truncate font-mono text-[10px] uppercase tracking-[0.24em] text-gold-soft">Compliance · Digital · Growth</p>
              </div>
            </div>
          </div>

          <div className="animate-float-y absolute left-2 top-6 flex items-center gap-2.5 rounded-2xl glass-panel px-4 py-3 sm:-left-8 sm:top-10" style={{ animationDelay: "0.6s" }}>
            <ShieldCheck className="h-5 w-5 text-gold" />
            <span className="text-xs font-semibold text-slate-200">FTA-Aligned Filing</span>
          </div>
          <div className="animate-float-y absolute right-2 top-1/3 flex items-center gap-2.5 rounded-2xl glass-panel px-4 py-3 sm:-right-6" style={{ animationDelay: "1.8s" }}>
            <Globe className="h-5 w-5 text-gold" />
            <span className="text-xs font-semibold text-slate-200">Web &amp; App Development</span>
          </div>
          <div className="animate-float-y absolute bottom-36 left-2 flex items-center gap-2.5 rounded-2xl glass-panel px-4 py-3 sm:-left-4 sm:bottom-24" style={{ animationDelay: "3s" }}>
            <TrendingUp className="h-5 w-5 text-gold" />
            <span className="text-xs font-semibold text-slate-200">Marketing That Converts</span>
          </div>
        </motion.div>
      </motion.div>

      <motion.a
        href="#about"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to about section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.1, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-gold-soft/70 transition-colors hover:text-gold-soft md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.a>
    </section>
  );
};
